/** Site Diary dashboard helpers: mark a job complete (asks admin to close it) and the collapsed "Marked complete" shelf. */
import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, ChevronDown, ChevronUp, Loader2, Undo2 } from 'lucide-react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '../../components/ui/alert-dialog';
import { PAPER, projectColour } from './theme';

export const isPendingComplete = (p) => p?.surveyor_archive_flag?.status === 'pending';

export function MarkCompleteButton({ api, project, onChanged, compact = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.post(`/diary/projects/${project.id}/complete-request`, {});
      toast.success(`${project.project_ref} marked complete — the office has been asked to close it`);
      setOpen(false); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not mark as complete'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1 rounded-full ${compact ? 'px-1.5 py-0.5' : 'px-2 py-1'} text-[10px] font-semibold uppercase tracking-wide transition-colors hover:bg-black/5 active:scale-95`}
        style={{ color: PAPER.inkSoft, border: `1px solid ${PAPER.border}` }} title="Mark this job as complete — asks the office to close it" data-testid={`diary-mark-complete-${project.id}`}>
        <CheckCircle2 className="w-3 h-3" /> {compact ? '' : 'Complete'}
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-[#1E2530] border-white/10 text-white" onClick={(e) => e.stopPropagation()} data-testid="diary-mark-complete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-400" /> Mark {project.project_ref} as complete?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              The office is asked to close the project. It moves to your "Marked complete" shelf and disappears from your diary once they set it to Complete — you can still log to it until then.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="diary-mark-complete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={run} disabled={busy} className="bg-emerald-500 text-white hover:bg-emerald-400" data-testid="diary-mark-complete-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Mark complete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function CompletedShelf({ api, projects, onOpen, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  if (!projects.length) return null;
  const undo = async (p) => {
    setBusy(p.id);
    try { await api.post(`/diary/projects/${p.id}/complete-request/undo`); toast.success(`${p.project_ref} is back in your live jobs`); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not undo'); }
    finally { setBusy(null); }
  };
  return (
    <div className="mt-4 rounded-xl overflow-hidden" style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }} data-testid="diary-completed-shelf">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2.5 text-left" data-testid="diary-completed-shelf-toggle">
        <span className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: PAPER.ink }}>
          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Marked complete · awaiting the office
          <span className="text-[11px] font-normal" style={{ color: PAPER.muted }}>({projects.length})</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4" style={{ color: PAPER.inkSoft }} /> : <ChevronDown className="w-4 h-4" style={{ color: PAPER.inkSoft }} />}
      </button>
      {open && (
        <div className="divide-y" style={{ borderColor: PAPER.border }}>
          {projects.map(p => {
            const flag = p.surveyor_archive_flag || {};
            return (
              <div key={p.id} className="flex items-center gap-2 px-3 py-2" data-testid={`diary-completed-row-${p.id}`}>
                <span className="w-1.5 self-stretch rounded-full" style={{ background: projectColour(p).hex }} />
                <button type="button" onClick={() => onOpen(p)} className="flex-1 min-w-0 text-left">
                  <div className="text-[13px] font-medium truncate" style={{ color: PAPER.ink }}>{p.project_ref}</div>
                  <div className="text-[10px]" style={{ color: PAPER.muted }}>marked {flag.requested_at ? new Date(flag.requested_at).toLocaleDateString('en-GB') : ''}{flag.requested_by_name ? ` by ${flag.requested_by_name}` : ''}</div>
                </button>
                <button type="button" onClick={() => undo(p)} disabled={busy === p.id} className="inline-flex items-center gap-1 text-[11px] underline disabled:opacity-50" style={{ color: PAPER.inkSoft }} data-testid={`diary-complete-undo-${p.id}`}>
                  {busy === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Undo
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
